import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { DocumentShareCredentials } from '@/entities/clinicalDocument/documentShareCredentials'
import type {
  ClinicalDocumentRecord,
  ClinicalDocumentType,
  PrescriptionTemplate,
} from '@/entities/clinicalDocument/types'
import { createId } from '@/shared/lib/id'
import { indexedDBJSON } from '@/shared/lib/persistence'
import { logClientError } from '@/shared/lib/safeClientLog'
import { normalizeClinicalDocumentType, normalizeDocumentDate } from '@/shared/lib/clinicalDocumentUtils'
import {
  clinicalDocumentsRepository,
  type ApiDocument,
  type ApiTemplate,
} from '@/shared/repositories/clinicalDocuments/clinicalDocumentsRepository'
import { clinicalDocumentShareTokensRepository } from '@/shared/repositories/clinicalDocumentShareTokens/clinicalDocumentShareTokensRepository'

export interface CreateDocumentPayload {
  patientId: string
  type: ClinicalDocumentType
  title: string
  summary: string
  content: string
  structuredFields?: ClinicalDocumentRecord['structuredFields']
  templateId?: string
  createdByUserId?: string
  prescriptionSignatureKind?: ClinicalDocumentRecord['prescriptionSignatureKind'] | null
  documentDate?: string
}

export interface CreateTemplatePayload {
  type: ClinicalDocumentType
  name: string
  description: string
}

interface ClinicalDocumentStoreState {
  documents: ClinicalDocumentRecord[]
  templates: PrescriptionTemplate[]
  loaded: boolean

  fetchData: () => Promise<void>
  getByPatientId: (patientId: string) => ClinicalDocumentRecord[]
  getByPatientIds: (patientIds: string[]) => ClinicalDocumentRecord[]
  getById: (documentId: string) => ClinicalDocumentRecord | undefined
  getByCreator: (userId: string) => ClinicalDocumentRecord[]

  addDocument: (payload: CreateDocumentPayload) => ClinicalDocumentRecord
  updateDocument: (
    documentId: string,
    payload: Partial<
      Pick<
        ClinicalDocumentRecord,
        | 'type'
        | 'title'
        | 'summary'
        | 'content'
        | 'structuredFields'
        | 'details'
        | 'templateId'
        | 'patientId'
        | 'prescriptionSignatureKind'
        | 'documentDate'
      >
    >,
  ) => ClinicalDocumentRecord | null
  removeDocument: (documentId: string) => Promise<void>
  /** Insere ou substitui um documento após resposta da API (ex.: gravar antes de assinar). */
  mergeDocumentFromServer: (record: ClinicalDocumentRecord) => void

  addTemplate: (payload: CreateTemplatePayload) => PrescriptionTemplate
  updateTemplate: (
    templateId: string,
    payload: Partial<Pick<PrescriptionTemplate, 'name' | 'description' | 'type'>>,
  ) => void
  removeTemplate: (templateId: string) => void
  /** Substitui ou insere um modelo após resposta da API (fluxo sem dupla gravação). */
  replaceTemplateAfterSync: (template: ApiTemplate) => void

  /** Token de partilha pública (cleartext só em memória; não persistido). */
  getDocumentShareCredentials: (documentId: string) => DocumentShareCredentials | null
  setDocumentShareCredentialsAfterSign: (documentId: string, creds: DocumentShareCredentials) => void
  clearDocumentShareCredentials: (documentId: string) => void
}

const sortByDateDesc = (documents: ClinicalDocumentRecord[]): ClinicalDocumentRecord[] =>
  [...documents].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  )

const apiDocToRecord = (
  d: ApiDocument,
  fallbackType?: ClinicalDocumentType,
): ClinicalDocumentRecord => ({
  id: d.id,
  patientId: d.patientId,
  type: normalizeClinicalDocumentType(d.type ?? fallbackType),
  title: d.title,
  summary: d.summary ?? '',
  prescriptionSignatureKind: d.prescriptionSignatureKind ?? undefined,
  details: d.details || d.content,
  content: d.content,
  structuredFields: d.structuredFields ?? null,
  templateId: d.templateId,
  createdByUserId: d.createdByUserId,
  createdAt: d.createdAt,
  updatedAt: d.updatedAt ?? d.createdAt,
  documentDate: normalizeDocumentDate(d.documentDate ?? d.createdAt),
})

/** Mapeia documento devolvido pela API para o registo da loja (pós-gravação síncrona). */
export const clinicalRecordFromApi = (
  d: ApiDocument,
  fallbackType?: ClinicalDocumentType,
): ClinicalDocumentRecord => apiDocToRecord(d, fallbackType)

const apiTemplateToRecord = (
  t: ApiTemplate,
  fallbackType: ClinicalDocumentType = 'prescription',
): PrescriptionTemplate => ({
  id: t.id,
  type: normalizeClinicalDocumentType(t.type ?? fallbackType),
  name: t.name,
  description: t.description,
  createdAt: t.createdAt,
  updatedAt: t.updatedAt,
})

export const useClinicalDocumentStore = create<ClinicalDocumentStoreState>()(
  persist(
    (set, get) => ({
      documents: [],
      templates: [],
      loaded: false,

      fetchData: async () => {
        try {
          const [docsRes, tplRes] = await Promise.all([
            clinicalDocumentsRepository.listDocuments(),
            clinicalDocumentsRepository.listTemplates(),
          ])
          set({
            documents: sortByDateDesc(docsRes.documents.map((document) => apiDocToRecord(document))),
            templates: tplRes.templates.map((template) => apiTemplateToRecord(template)),
            loaded: true,
          })
        } catch (error) {
          logClientError('[login] fetchData (clinical documents): falha ao carregar', error)
          /* keep local state */
        }
      },

      getByPatientId: (patientId) =>
        sortByDateDesc(get().documents.filter((d) => d.patientId === patientId)),

      getByPatientIds: (patientIds) => {
        const idSet = new Set(patientIds)
        return sortByDateDesc(get().documents.filter((d) => idSet.has(d.patientId)))
      },

      getById: (documentId) => get().documents.find((d) => d.id === documentId),

      getByCreator: (userId) =>
        sortByDateDesc(get().documents.filter((d) => d.createdByUserId === userId)),

      addDocument: (payload) => {
        const now = new Date().toISOString()
        const documentDate = normalizeDocumentDate(payload.documentDate)
        const cleanTitle = payload.title.trim()
        const cleanContent = payload.content.trim()
        const cleanSummary = payload.summary.trim()
        const normalizedType = normalizeClinicalDocumentType(payload.type)
        const record: ClinicalDocumentRecord = {
          id: createId(),
          patientId: payload.patientId,
          type: normalizedType,
          title: cleanTitle,
          summary: cleanSummary,
          content: cleanContent,
          structuredFields: payload.structuredFields ?? null,
          details: cleanContent,
          templateId: payload.templateId,
          prescriptionSignatureKind:
            normalizedType === 'prescription'
              ? (payload.prescriptionSignatureKind ?? 'prescription_simple')
              : null,
          createdByUserId: payload.createdByUserId,
          createdAt: now,
          updatedAt: now,
          documentDate,
        }

        set((state) => ({
          documents: sortByDateDesc([record, ...state.documents]),
        }))

        clinicalDocumentsRepository
          .createDocument({
            patientId: payload.patientId,
            type: payload.type,
            title: cleanTitle,
            summary: cleanSummary,
            content: cleanContent,
            structuredFields: record.structuredFields,
            templateId: payload.templateId,
            prescriptionSignatureKind:
              normalizedType === 'prescription'
                ? (payload.prescriptionSignatureKind ?? 'prescription_simple')
                : null,
            documentDate,
          })
          .then((res) => {
            set((state) => ({
              documents: state.documents.map((d) =>
                d.id === record.id ? apiDocToRecord(res.document, record.type) : d,
              ),
            }))
          })
          .catch(() => {})

        return record
      },

      updateDocument: (documentId, payload) => {
        const current = get().documents.find((document) => document.id === documentId)
        if (!current) return null

        const now = new Date().toISOString()
        const nextTitle = payload.title !== undefined ? payload.title.trim() : current.title
        const nextContent = payload.content !== undefined ? payload.content.trim() : current.content
        const nextSummary =
          payload.summary !== undefined ? payload.summary.trim() : current.summary
        const nextType =
          payload.type !== undefined
            ? normalizeClinicalDocumentType(payload.type)
            : current.type
        const nextPrescriptionKind =
          nextType === 'prescription'
            ? (payload.prescriptionSignatureKind !== undefined
                ? payload.prescriptionSignatureKind
                : (current.prescriptionSignatureKind ?? 'prescription_simple'))
            : null
        const nextDocumentDate =
          payload.documentDate !== undefined
            ? normalizeDocumentDate(payload.documentDate)
            : normalizeDocumentDate(current.documentDate ?? current.createdAt)

        const updatedDocument: ClinicalDocumentRecord = {
          ...current,
          patientId: payload.patientId ?? current.patientId,
          type: nextType,
          title: nextTitle,
          summary: nextSummary,
          content: nextContent,
          structuredFields: payload.structuredFields !== undefined ? payload.structuredFields : current.structuredFields,
          details: payload.details !== undefined ? payload.details : nextContent,
          templateId:
            payload.templateId !== undefined ? payload.templateId : current.templateId,
          prescriptionSignatureKind: nextPrescriptionKind,
          documentDate: nextDocumentDate,
          updatedAt: now,
        }

        set((state) => ({
          documents: sortByDateDesc(
            state.documents.map((document) =>
              document.id === documentId ? updatedDocument : document,
            ),
          ),
        }))

        clinicalDocumentsRepository
          .updateDocument(documentId, {
            patientId: updatedDocument.patientId,
            type: updatedDocument.type,
            title: updatedDocument.title,
            summary: updatedDocument.summary,
            content: updatedDocument.content,
            structuredFields: updatedDocument.structuredFields,
            templateId: updatedDocument.templateId,
            prescriptionSignatureKind: updatedDocument.prescriptionSignatureKind ?? null,
            documentDate: updatedDocument.documentDate,
          })
          .then((res) => {
            set((state) => ({
              documents: sortByDateDesc(
                state.documents.map((document) =>
                  document.id === documentId ? apiDocToRecord(res.document, updatedDocument.type) : document,
                ),
              ),
            }))
          })
          .catch(() => {})

        return updatedDocument
      },

      removeDocument: async (documentId) => {
        await clinicalDocumentsRepository.removeDocument(documentId)
        set((state) => ({
          documents: state.documents.filter((d) => d.id !== documentId),
        }))
      },

      mergeDocumentFromServer: (record) => {
        set((state) => ({
          documents: sortByDateDesc([record, ...state.documents.filter((d) => d.id !== record.id)]),
        }))
      },

      addTemplate: (payload) => {
        const now = new Date().toISOString()
        const template: PrescriptionTemplate = {
          id: createId(),
          type: normalizeClinicalDocumentType(payload.type),
          name: payload.name.trim(),
          description: payload.description.trim(),
          createdAt: now,
          updatedAt: now,
        }

        set((state) => ({
          templates: [template, ...state.templates],
        }))

        clinicalDocumentsRepository
          .createTemplate({
            type: payload.type,
            name: payload.name.trim(),
            description: payload.description.trim(),
          })
          .then((res) => {
            set((state) => ({
              templates: state.templates.map((t) =>
                t.id === template.id ? apiTemplateToRecord(res.template, template.type) : t,
              ),
            }))
          })
          .catch(() => {})

        return template
      },

      updateTemplate: (templateId, payload) => {
        set((state) => ({
          templates: state.templates.map((t) => {
            if (t.id !== templateId) return t
            return {
              ...t,
              type: payload.type ? normalizeClinicalDocumentType(payload.type) : t.type,
              name: payload.name?.trim() ?? t.name,
              description: payload.description?.trim() ?? t.description,
              updatedAt: new Date().toISOString(),
            }
          }),
        }))
        clinicalDocumentsRepository
          .updateTemplate(templateId, {
            name: payload.name?.trim(),
            description: payload.description?.trim(),
          })
          .then((res) => {
            set((state) => ({
              templates: state.templates.map((template) =>
                template.id === templateId ? apiTemplateToRecord(res.template, template.type) : template,
              ),
            }))
          })
          .catch(() => {})
      },

      removeTemplate: (templateId) => {
        set((state) => ({
          templates: state.templates.filter((t) => t.id !== templateId),
        }))
        clinicalDocumentsRepository.removeTemplate(templateId).catch(() => {})
      },

      replaceTemplateAfterSync: (template) => {
        const record = apiTemplateToRecord(template)
        set((state) => ({
          templates: [record, ...state.templates.filter((t) => t.id !== record.id)],
        }))
      },

      getDocumentShareCredentials: (documentId) =>
        clinicalDocumentShareTokensRepository.getMintedThisSession(documentId),

      setDocumentShareCredentialsAfterSign: (documentId, creds) => {
        clinicalDocumentShareTokensRepository.rememberMinted(documentId, creds)
      },

      clearDocumentShareCredentials: (documentId) => {
        clinicalDocumentShareTokensRepository.clearSessionCache(documentId)
      },
    }),
    {
      name: 'synap-clinical-document-store',
      version: 2,
      storage: indexedDBJSON,
      partialize: (state) => ({ documents: state.documents, templates: state.templates }),
      skipHydration: true,
      migrate: (persistedState) => {
        const state = (persistedState ?? {}) as Partial<
          Pick<ClinicalDocumentStoreState, 'documents' | 'templates'>
        >
        const documents = Array.isArray(state.documents)
          ? state.documents.map((document) => ({
              ...document,
              type: normalizeClinicalDocumentType(document.type),
            }))
          : []
        const templates = Array.isArray(state.templates)
          ? state.templates.map((template) => ({
              ...template,
              type: normalizeClinicalDocumentType(template.type),
            }))
          : []
        return { documents, templates }
      },
    },
  ),
)
